import React, { useState } from 'react';
import { 
  Brain, 
  Code, 
  Database, 
  Cloud, 
  Shield, 
  Cpu, 
  LineChart, 
  Workflow 
} from 'lucide-react';
import SolutionsDialog from '../solutions/SolutionsDialog';

const technologies = [
  {
    id: 1,
    title: 'AI & Machine Learning',
    description: 'State-of-the-art AI models and algorithms',
    icon: Brain,
    items: ['TensorFlow', 'PyTorch', 'OpenAI', 'Custom ML Models'],
    gradient: 'from-purple-600 to-indigo-600'
  },
  {
    id: 2,
    title: 'Frontend Development',
    description: 'Modern, responsive web interfaces',
    icon: Code,
    items: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS'],
    gradient: 'from-blue-600 to-cyan-600'
  },
  {
    id: 3,
    title: 'Backend Systems',
    description: 'Scalable and reliable server architecture',
    icon: Database,
    items: ['Node.js', 'Python', 'PostgreSQL', 'MongoDB'],
    gradient: 'from-green-600 to-teal-600'
  },
  {
    id: 4,
    title: 'Cloud Infrastructure',
    description: 'Enterprise-grade cloud solutions',
    icon: Cloud,
    items: ['AWS', 'Azure', 'Google Cloud', 'Serverless'],
    gradient: 'from-orange-600 to-red-600'
  },
  {
    id: 5,
    title: 'Security',
    description: 'Advanced security measures',
    icon: Shield,
    items: ['End-to-end Encryption', 'OAuth 2.0', 'JWT', 'Regular Audits'],
    gradient: 'from-red-600 to-pink-600'
  },
  {
    id: 6,
    title: 'Data Processing',
    description: 'High-performance data handling',
    icon: Cpu,
    items: ['Real-time Processing', 'Big Data', 'Stream Analytics', 'ETL'],
    gradient: 'from-violet-600 to-purple-600'
  },
  {
    id: 7,
    title: 'Analytics',
    description: 'Comprehensive data insights',
    icon: LineChart,
    items: ['Custom Metrics', 'Dashboards', 'Predictive Analytics', 'Reports'],
    gradient: 'from-yellow-600 to-amber-600'
  },
  {
    id: 8,
    title: 'Development Process',
    description: 'Agile methodology',
    icon: Workflow,
    items: ['CI/CD', 'Agile', 'Test-Driven', 'Rapid Iteration'],
    gradient: 'from-emerald-600 to-green-600'
  }
];

export default function TechnologyShowcase() {
  const [showConsultation, setShowConsultation] = useState(false);

  return (
    <section className="py-16 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
            Our Technology Stack
          </h2>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-300">
            Cutting-edge technologies powering your digital transformation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {technologies.map((tech) => {
            const Icon = tech.icon;
            return (
              <div
                key={tech.id}
                className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className={`p-6 bg-gradient-to-r ${tech.gradient} group-hover:scale-105 transition-transform duration-300`}>
                  <Icon className="w-8 h-8 text-white mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {tech.title}
                  </h3>
                  <p className="text-white text-opacity-90 text-sm">
                    {tech.description}
                  </p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {tech.items.map((item, index) => (
                      <li 
                        key={index}
                        className="flex items-center text-gray-700 dark:text-gray-300"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <button
            onClick={() => setShowConsultation(true)}
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-300"
          >
            Start Your Project
          </button>
        </div>

        <SolutionsDialog 
          isOpen={showConsultation}
          onClose={() => setShowConsultation(false)}
        />
      </div>
    </section>
  );
}